import React, { useState } from "react";
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Image } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { format } from "date-fns";
import { useTheme } from "../../../context/ThemeContext";

const EventDetails = ({ event, onClose }) => {
	const [showTicketModal, setShowTicketModal] = useState(false);
	const theme = useTheme();
	const styles = useStyles();

	const handleRSVP = () => {
		// Handle RSVP logic
		setShowTicketModal(true);
	};

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<TouchableOpacity style={styles.closeButton} onPress={onClose}>
					<Text style={styles.closeButtonText}>Close</Text>
				</TouchableOpacity>
			</View>

			{event.coverImage && false && <Image source={{ uri: event.coverImage }} style={styles.coverImage} />}

			<View style={styles.contentContainer}>
				<ScrollView style={styles.content}>
					<View style={styles.titleSection}>
						<Text style={styles.title}>{event.title}</Text>
						<View style={styles.organizerInfo}>
							<Image source={{ uri: event.organizer.avatar }} style={styles.organizerAvatar} />
							<Text style={styles.organizerName}>by {event.organizer.name}</Text>
						</View>
					</View>

					<View style={styles.detailsSection}>
						<View style={styles.detailItem}>
							<Ionicons name="calendar-outline" size={20} color={theme.colors.textSecondary} />
							<View>
								<Text style={styles.detailText}>{format(new Date(event.date), "EEEE, MMMM d, yyyy")}</Text>
								<Text style={styles.detailText}>
									{format(new Date(event.date), "h:mm a")} - {format(new Date(event.endDate), "h:mm a")}
								</Text>
							</View>
						</View>

						<View style={styles.detailItem}>
							<Ionicons name="location-outline" size={20} color={theme.colors.textSecondary} />
							<View>
								<Text style={styles.detailText}>{event.location}</Text>
								<Text style={styles.detailSubtext}>{event.address}</Text>
							</View>
						</View>

						{event.isPaid && (
							<View style={styles.detailItem}>
								<Ionicons name="ticket-outline" size={20} color={theme.colors.textSecondary} />
								<View>
									<Text style={styles.detailLabel}>Price</Text>
									<Text style={styles.detailText}>${event.price}</Text>
									<Text style={styles.detailSubtext}>{event.ticketsRemaining} tickets remaining</Text>
								</View>
							</View>
						)}
					</View>

					<View style={styles.section}>
						<Text style={styles.sectionTitle}>About this event</Text>
						<Text style={styles.description}>{event.description}</Text>
					</View>

					{event.schedule && (
						<View style={styles.section}>
							<Text style={styles.sectionTitle}>Schedule</Text>
							{event.schedule.map((item, index) => (
								<View key={index} style={styles.scheduleItem}>
									<Text style={styles.scheduleTime}>{item.time}</Text>
									<Text style={styles.scheduleActivity}>{item.activity}</Text>
								</View>
							))}
						</View>
					)}

					{event.tags && (
						<View style={styles.tagsSection}>
							{event.tags.map((tag) => (
								<View key={tag} style={styles.tag}>
									<Text style={styles.tagText}>{tag}</Text>
								</View>
							))}
						</View>
					)}
				</ScrollView>
			</View>

			<View style={styles.footer}>
				<TouchableOpacity style={[styles.rsvpButton, event.isPaid && styles.ticketButton]} onPress={handleRSVP}>
					<View style={styles.rsvpContent}>
						<Text style={styles.rsvpButtonText}>{event.isPaid ? "Purchase Tickets" : "RSVP Now"}</Text>
						{event.attendees && (
							<View style={styles.attendeesPreview}>
								{event.attendees.slice(0, 3).map((attendee, index) => (
									<Image key={attendee.id} source={{ uri: attendee.avatar }} style={[styles.attendeeAvatar, { marginLeft: index > 0 ? -12 : 0 }]} />
								))}
								{event.attendees.length > 3 && (
									<View style={[styles.attendeeAvatar, styles.attendeeCount, { marginLeft: -12 }]}>
										<Text style={styles.attendeeCountText}>+{event.attendees.length - 3}</Text>
									</View>
								)}
							</View>
						)}
					</View>
					<Text style={styles.rsvpButtonPrice}>{event.isPaid ? `$${event.price}` : "Free"}</Text>
				</TouchableOpacity>
			</View>

			{showTicketModal && (
				<View style={styles.modalOverlay}>
					<View style={styles.modal}>
						<View style={styles.modalHeader}>
							<Text style={styles.modalTitle}>{event.isPaid ? "Purchase Tickets" : "RSVP"}</Text>
							<TouchableOpacity onPress={() => setShowTicketModal(false)}>
								<Ionicons name="close" size={24} color={theme.colors.textSecondary} />
							</TouchableOpacity>
						</View>

						{/* Add ticket selection or RSVP form here */}
						<View style={styles.modalContent}>
							{event.isPaid ? (
								<View style={styles.ticketOptions}>
									<Text style={styles.ticketType}>General Admission</Text>
									<View style={styles.ticketQuantity}>
										<TouchableOpacity style={styles.quantityButton}>
											<Ionicons name="remove" size={20} color={theme.colors.textSecondary} />
										</TouchableOpacity>
										<Text style={styles.quantityText}>1</Text>
										<TouchableOpacity style={styles.quantityButton}>
											<Ionicons name="add" size={20} color={theme.colors.textSecondary} />
										</TouchableOpacity>
									</View>
								</View>
							) : (
								<View style={styles.rsvpForm}>
									<Text style={styles.rsvpText}>Would you like to attend this event?</Text>
								</View>
							)}
						</View>

						<View style={styles.modalActions}>
							<TouchableOpacity style={styles.modalCancelButton} onPress={() => setShowTicketModal(false)}>
								<Text style={styles.modalCancelText}>Cancel</Text>
							</TouchableOpacity>
							<TouchableOpacity style={styles.modalConfirmButton}>
								<Text style={styles.modalConfirmText}>{event.isPaid ? "Purchase" : "Confirm RSVP"}</Text>
							</TouchableOpacity>
						</View>
					</View>
				</View>
			)}
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			flex: 1,
			backgroundColor: theme.colors.systemBackground.primary,
		},
		header: {
			position: "absolute",
			top: 0,
			right: theme.spacing.md,
			zIndex: 10,
			padding: theme.spacing.md,
		},
		closeButton: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.primary,
			paddingVertical: 8,
			paddingHorizontal: 10,
			borderRadius: 5,
		},
		closeButtonText: {
			color: "white",
			fontSize: 14,
			fontWeight: "500",
		},
		coverImage: {
			width: "100%",
			height: 200,
			resizeMode: "cover",
		},
		contentContainer: {
			flex: 1,
			paddingBottom: 80,
		},
		content: {
			flexGrow: 1,
		},
		titleSection: {
			padding: theme.spacing.md,
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.separator,
			backgroundColor: theme.colors.systemBackground.primary,
		},
		title: {
			...theme.typography.title1,
			color: theme.colors.text,
			marginBottom: theme.spacing.sm,
		},
		organizerInfo: {
			flexDirection: "row",
			alignItems: "center",
		},
		organizerAvatar: {
			width: 32,
			height: 32,
			borderRadius: 16,
			marginRight: theme.spacing.sm,
		},
		organizerName: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
		detailsSection: {
			backgroundColor: theme.colors.systemBackground.secondary,
			padding: theme.spacing.md,
			gap: theme.spacing.md,
		},
		detailItem: {
			flexDirection: "row",
			gap: theme.spacing.sm,
		},
		detailLabel: {
			...theme.typography.caption2,
			color: theme.colors.textSecondary,
			marginBottom: theme.spacing.xs,
		},
		detailText: {
			...theme.typography.subhead,
			color: theme.colors.text,
			fontWeight: "500",
		},
		detailSubtext: {
			...theme.typography.caption1,
			color: theme.colors.textSecondary,
			marginTop: theme.spacing.xs,
		},
		section: {
			padding: theme.spacing.md,
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.separator,
		},
		sectionTitle: {
			...theme.typography.title3,
			color: theme.colors.text,
			marginBottom: theme.spacing.sm,
		},
		description: {
			...theme.typography.body,
			color: theme.colors.text,
			lineHeight: 24,
		},
		scheduleItem: {
			flexDirection: "row",
			marginBottom: theme.spacing.sm,
			paddingBottom: theme.spacing.sm,
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.separator,
		},
		scheduleTime: {
			width: 80,
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			fontWeight: "500",
		},
		scheduleActivity: {
			flex: 1,
			...theme.typography.body,
			color: theme.colors.text,
		},
		tagsSection: {
			flexDirection: "row",
			flexWrap: "wrap",
			gap: theme.spacing.sm,
			padding: theme.spacing.md,
		},
		tag: {
			backgroundColor: theme.colors.gray[6],
			paddingHorizontal: theme.spacing.sm,
			paddingVertical: theme.spacing.xs,
			borderRadius: theme.borderRadius.pill,
		},
		tagText: {
			...theme.typography.caption2,
			color: theme.colors.textSecondary,
		},
		footer: {
			position: "absolute",
			bottom: 0,
			left: 0,
			right: 0,
			padding: theme.spacing.md,
			borderTopWidth: 1,
			borderTopColor: theme.colors.separator,
			backgroundColor: theme.colors.systemBackground.primary,
			...theme.shadows.sm,
		},
		rsvpButton: {
			backgroundColor: theme.colors.primary,
			borderRadius: theme.borderRadius.lg,
			padding: theme.spacing.md,
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
		},
		ticketButton: {
			backgroundColor: theme.colors.success,
		},
		rsvpContent: {
			flex: 1,
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.md,
		},
		rsvpButtonText: {
			...theme.typography.headline,
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		rsvpButtonPrice: {
			...theme.typography.headline,
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		attendeesPreview: {
			flexDirection: "row",
			alignItems: "center",
		},
		attendeeAvatar: {
			width: 24,
			height: 24,
			borderRadius: 12,
			borderWidth: 2,
			borderColor: "white",
		},
		attendeeCount: {
			backgroundColor: "white",
			justifyContent: "center",
			alignItems: "center",
			width: "auto",
			paddingHorizontal: theme.spacing.sm,
			borderRadius: 12,
		},
		attendeeCountText: {
			fontSize: 10,
			color: theme.colors.text,
			fontWeight: "600",
		},
		modalOverlay: {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: "rgba(0, 0, 0, 0.5)",
			justifyContent: "center",
			alignItems: "center",
		},
		modal: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.lg,
			width: "90%",
			maxWidth: 400,
			padding: theme.spacing.lg,
			...theme.shadows.md,
		},
		modalHeader: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: theme.spacing.lg,
		},
		modalTitle: {
			...theme.typography.title2,
			color: theme.colors.text,
		},
		modalContent: {
			marginBottom: theme.spacing.lg,
		},
		ticketOptions: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			paddingVertical: theme.spacing.sm,
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.separator,
		},
		ticketType: {
			...theme.typography.body,
			color: theme.colors.text,
		},
		ticketQuantity: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.sm,
		},
		quantityButton: {
			width: 32,
			height: 32,
			borderRadius: 16,
			backgroundColor: theme.colors.gray[6],
			justifyContent: "center",
			alignItems: "center",
		},
		quantityText: {
			...theme.typography.body,
			color: theme.colors.text,
			fontWeight: "500",
		},
		rsvpForm: {
			alignItems: "center",
		},
		rsvpText: {
			...theme.typography.body,
			color: theme.colors.text,
			textAlign: "center",
		},
		modalActions: {
			flexDirection: "row",
			justifyContent: "flex-end",
			gap: theme.spacing.sm,
		},
		modalCancelButton: {
			paddingVertical: theme.spacing.sm,
			paddingHorizontal: theme.spacing.md,
			borderRadius: theme.borderRadius.md,
			backgroundColor: theme.colors.gray[6],
		},
		modalCancelText: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			fontWeight: "500",
		},
		modalConfirmButton: {
			paddingVertical: theme.spacing.sm,
			paddingHorizontal: theme.spacing.md,
			borderRadius: theme.borderRadius.md,
			backgroundColor: theme.colors.primary,
		},
		modalConfirmText: {
			...theme.typography.subhead,
			color: theme.colors.systemBackground.primary,
			fontWeight: "500",
		},
	});
};

export default EventDetails;
